import React, { useState, useEffect } from 'react';
import { TextField, Button, Box, Typography, Grid, CircularProgress, Alert } from '@mui/material';
import axios from 'axios';
import Header1 from '../Header/Header1';
import Footer2 from '../Footer/Footer2';

const API_URL = process.env.REACT_APP_API_URL;

const BankVerificationForm = ({ userId, onLogout }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [bankAccount, setBankAccount] = useState('');
  const [ifsc, setIfsc] = useState('');
  const [pan, setPan] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [verificationData, setVerificationData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [formErrors, setFormErrors] = useState({ bankAccount: false, ifsc: false, pan: false });

  useEffect(() => {
    if (userId) {
      axios
        .get(`${API_URL}/api/bank-verification/`, {
          params: { user_id: userId },
        })
        .then((response) => {
          const data = response.data;
          setVerificationData(data);
          setBankAccount(data.bank_account || '');
          setIfsc(data.ifsc || '');
          setPan(data.pan || '');
          setLoading(false);
        })
        .catch(() => {
          setVerificationData(null);
          setLoading(false);
        });
    }
  }, [userId]);

  const handleSubmit = () => {
    const hasErrors = {
      bankAccount: !bankAccount,
      ifsc: !ifsc,
      pan: !pan,
    };
    setFormErrors(hasErrors);

    if (hasErrors.bankAccount || hasErrors.ifsc || hasErrors.pan) return;

    const apiUrl = verificationData
      ? `${API_URL}/api/bank-verification/update/`
      : `${API_URL}/api/bank-verification/create/`;
    const requestMethod = verificationData ? axios.patch : axios.post;

    requestMethod(apiUrl, {
      user_id: userId,
      bank_account: bankAccount,
      ifsc: ifsc,
      pan: pan,
    })
      .then((response) => {
        setSuccessMessage(response.data.detail);
        setIsEditing(false);
        setFormErrors({ bankAccount: false, ifsc: false, pan: false });
      })
      .catch(() => {
        setError('Error saving verification data.');
      });
  };

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <div>
         <Header1 userId={userId} onLogout={onLogout} />
         <Box
  sx={{
    backgroundColor: '#2222',
    minHeight: '30vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: 3,
    maxWidth: { xs: '100%', sm: '70%', md: '50%', lg: '40%' },
    margin: '0 auto',
    my: 25, // Adds margin-top and margin-bottom
  }}
>

      <Typography sx={{ fontSize: '2rem', marginBottom: 3, textAlign: 'center', color: '#e0e0e0', letterSpacing: 5 }}>
        {isEditing || !verificationData ? 'Enter or Edit Your Bank Details' : 'Bank Details'}
      </Typography>

      {verificationData && !isEditing && (
        <Box sx={{ marginBottom: 3, textAlign: 'center' }}>
          <Typography sx={infoTextStyle}><strong>Bank Account No:</strong> <span>{verificationData.bank_account}</span></Typography>
          <Typography sx={infoTextStyle}><strong>IFSC:</strong> <span>{verificationData.ifsc}</span></Typography>
          <Typography sx={infoTextStyle}><strong>PAN:</strong> <span>{verificationData.pan}</span></Typography>

          <Button
            sx={buttonStyle}
            onClick={() => setIsEditing(true)}
          >
            Edit Information
          </Button>
        </Box>
      )}

      {(isEditing || !verificationData) && (
        <Grid container spacing={2} sx={{ justifyContent: 'center', maxWidth: '600px', width: '100%' }}>
          <Grid item xs={12}>
            <TextField
              label="Bank Account Number"
              variant="outlined"
              fullWidth
              value={bankAccount}
              onChange={(e) => setBankAccount(e.target.value)}
              error={formErrors.bankAccount}
              helperText={formErrors.bankAccount ? 'Bank account number is required' : ''}
              sx={inputStyle}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="IFSC Code"
              variant="outlined"
              fullWidth
              value={ifsc}
              onChange={(e) => setIfsc(e.target.value)}
              error={formErrors.ifsc}
              helperText={formErrors.ifsc ? 'IFSC code is required' : ''}
              sx={inputStyle}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="PAN Number"
              variant="outlined"
              fullWidth
              value={pan}
              onChange={(e) => setPan(e.target.value)}
              error={formErrors.pan}
              helperText={formErrors.pan ? 'PAN number is required' : ''}
              sx={inputStyle}
            />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button sx={buttonStyle} onClick={handleSubmit}>
                {verificationData ? 'Update Information' : 'Submit Information'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ marginTop: 2, width: '100%', letterSpacing: 3 }}>
          {successMessage}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ marginTop: 2, width: '100%', letterSpacing: 3 }}>
          {error}
        </Alert>
      )}
    </Box>
    <Footer2/>
    </div>
  );
};

const inputStyle = { marginBottom: 2, letterSpacing: 3 };
const buttonStyle = {
  backgroundColor: '#ff8c00',
  color: '#fff',
  fontWeight: 'bold',
  padding: '0.8rem 1.5rem',
  letterSpacing: 4,
  borderRadius: '30px',
  '&:hover': { backgroundColor: '#ff6f00' },
};
const infoTextStyle = {
  color: '#e0e0e0',
  fontSize: { xs: '1rem', sm: '1.1rem' },
  fontWeight: 400,
  lineHeight: 1.5,
  letterSpacing: 4,
  marginBottom: 2,
  display: 'flex',
  alignItems: 'center',
};

export default BankVerificationForm;
